/**
 * Sprite fonts SSOT — CraftPix Classic Sevens alphabet packs.
 *
 * Disk: client/public/fonts/sprite/sevens/{color}/*.png
 * Deploy: wrangler assets (same paths under /fonts/sprite/sevens/)
 *
 * Web UI fonts (Bebas Neue / DM Sans / Inter) stay for body text.
 * Use sprite fonts for arcade marquee, win amounts, SPIN chrome.
 */

export const SPRITE_FONT_SEVENS_BASE = "/fonts/sprite/sevens";

export type SevensFontColor =
  | "blue"
  | "green"
  | "red"
  | "white"
  | "yellow"
  | "yellow-light";

export const SEVENS_FONT_COLORS: readonly SevensFontColor[] = [
  "blue",
  "green",
  "red",
  "white",
  "yellow",
  "yellow-light",
] as const;

/** Native glyph canvas size from kit PNGs */
export const SEVENS_GLYPH_NATIVE = { w: 572, h: 635 } as const;

/**
 * Map a display character → filename under sevens/{color}/
 * Unknown letters → null (caller may skip or use fallback text).
 */
export function sevensGlyphFile(char: string): string | null {
  if (!char || char.length === 0) return null;
  if (char === " ") return null; // space = gap only
  if (char === ".") return "Dot.png";
  if (char === "?") return "question.png";
  if (char === "!") return "!.png";
  if (char === "+") return "+.png";
  if (char === ",") return ",.png";
  if (char === "-") return "-.png";
  if (char === "×" || char === "x" || char === "X") return "X.png";
  if (char === "·") return "Dot.png";

  const upper = char.toUpperCase();
  if (/^[A-Z]$/.test(upper)) return `${upper}.png`;
  if (/^[0-9]$/.test(char)) return `${char}.png`;
  return null;
}

export function sevensGlyphUrl(
  char: string,
  color: SevensFontColor = "yellow",
): string | null {
  const file = sevensGlyphFile(char);
  if (!file) return null;
  return `${SPRITE_FONT_SEVENS_BASE}/${color}/${encodeURIComponent(file)}`;
}

/** Preload a full color pack (call once on slots enter). */
export function preloadSevensFont(
  color: SevensFontColor = "yellow",
): void {
  if (typeof window === "undefined") return;
  const sample = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!+-?.";
  for (const ch of sample) {
    const url = sevensGlyphUrl(ch, color);
    if (!url) continue;
    const img = new Image();
    img.decoding = "async";
    img.src = url;
  }
}

export const SPRITE_FONTS_SSOT = {
  sevens: {
    id: "craftpix-classic-sevens",
    base: SPRITE_FONT_SEVENS_BASE,
    colors: SEVENS_FONT_COLORS,
    native: SEVENS_GLYPH_NATIVE,
    manifest: `${SPRITE_FONT_SEVENS_BASE}/manifest.json`,
    sourceKit: "craftpix-781165-classic-sevens-slots-2d-game-kit",
    webDisplay: "Bebas Neue / DM Sans (index.html) — body UI",
  },
} as const;
